<script>
// Read-only prose that Demo Mode can show as a readable rewrite instead of a
// redaction. Off: exactly the original text (through `renderer` when given).
// On: redacted like any `.sensitive[data-demo-blur]` element until a rewrite
// for this exact text, record, tenant, user and Demo generation has come back
// from utils/demoRewrite and passed the browser checks; then the rewrite is
// rendered through the same renderer and registered with the engine, which
// drops the redaction in the same pass. Any mismatch keeps it redacted.
//
// The rewrite is display-only component state: never emitted, stored,
// tracked or copied. Not for editable controls or interactive containers.
//
//   <DemoText tag="p" class="text-xs" :text="note.text" :record="patientId" />
//   <DemoText tag="li" class="brief-rich" :text="fact" :renderer="formatText" />
//
// `renderer` is an existing sanitizing formatter (text -> HTML), applied
// exactly as the element applied it before; without one the text renders as
// plain text. A render function rather than a template: `tag` is any plain
// element name and the root must stay a single element.
import { h, toDisplayString } from 'vue';
import store from '@/store';
import {
  clearDemoRewrite,
  demoBrandText,
  onDemoNameMapChange,
  registerDemoRewrite,
} from '@/utils/demoSpoof';
import {
  TRANSIENT_REASONS,
  checkRewrite,
  describeRewrite,
  noteDemoTextReadable,
  trackDemoTextCensus,
  untrackDemoTextCensus,
  reportRejectedRewrite,
  requestDemoRewrite,
} from '@/utils/demoRewrite';

export default {
  name: 'DemoText',
  props: {
    // Any value, shown exactly as `{{ text }}` would show it (objects and
    // arrays as JSON), so a converted site renders the same as before.
    text: { type: null, default: '' },
    // Raw patient identifier the prose belongs to, or null. Sent to the
    // rewrite endpoint only; never rendered.
    record: { type: [String, Number], default: null },
    tag: { type: String, default: 'span' },
    renderer: { type: Function, default: null },
  },
  data() {
    return {
      rewrite: null,
    };
  },
  computed: {
    // The store module directly, not this.$store: DemoText also renders in
    // trees mounted without the store plugin.
    isDemoMode() {
      return store.getters['demoMode/isDemoMode'] !== false;
    },
    generation() {
      return store.getters['demoMode/generation'];
    },
    source() {
      return toDisplayString(this.text);
    },
    recordId() {
      return this.record == null || this.record === '' ? null : String(this.record);
    },
    context() {
      return [
        store.getters['tenant/current_tenant_slug'] ?? null,
        store.getters['auth/current_user']?.id ?? null,
        this.generation,
      ].join('|');
    },
    requestIdentity() {
      return JSON.stringify([this.isDemoMode, this.context, this.recordId, this.source]);
    },
    // A rewrite is shown only for the source it was made from, in the
    // context it was made in, while Demo Mode is on.
    showingRewrite() {
      const r = this.rewrite;
      return Boolean(
        this.isDemoMode && r && r.source === this.source && r.record === this.recordId && r.context === this.context
      );
    },
    shownText() {
      return this.showingRewrite ? this.rewrite.text : this.source;
    },
    html() {
      return this.renderer ? this.renderer(this.shownText) || '' : '';
    },
  },
  watch: {
    requestIdentity() {
      this.sync();
    },
    showingRewrite: {
      handler() {
        this.syncRegistration();
      },
      flush: 'post',
    },
  },
  created() {
    this.active = false;
    this.cancelRequest = null;
    this.pendingKey = null;
    this.failedKey = null;
    this.failReason = null;
    this.failDiagnostics = null;
    this.registered = false;
    this.stopNameMap = null;
  },
  mounted() {
    this.start();
  },
  activated() {
    this.start();
  },
  updated() {
    this.syncRegistration();
  },
  deactivated() {
    this.stop();
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    start() {
      if (this.active) return;
      this.active = true;
      trackDemoTextCensus(this.$el, () => this.censusState(), () => this.failDiagnostics);
      this.stopNameMap = onDemoNameMapChange(() => this.onNameMapChange());
      // A mount or reactivation is the one moment a transient failure (429,
      // timeout, network) may be tried again; the queue holds the per-block
      // budget and any cooldown. Terminal outcomes stay as they were.
      if (TRANSIENT_REASONS.includes(this.failReason)) {
        this.failedKey = null;
        this.failReason = null;
      }
      this.sync({ allowRetry: true });
    },
    stop() {
      this.active = false;
      untrackDemoTextCensus(this.$el);
      this.stopNameMap?.();
      this.stopNameMap = null;
      this.abandonRequest();
      this.rewrite = null;
      if (this.registered) {
        this.registered = false;
        clearDemoRewrite(this.$el);
      }
    },
    abandonRequest({ superseded = false } = {}) {
      if (this.cancelRequest) this.cancelRequest({ superseded });
      this.cancelRequest = null;
      this.pendingKey = null;
    },
    describe() {
      return describeRewrite({ text: this.source, record: this.recordId });
    },
    // `allowRetry` only from start(): never on a re-render, a watcher or a
    // name-map change.
    sync({ allowRetry = false } = {}) {
      this.abandonRequest({ superseded: true });
      if (!this.active || !this.isDemoMode) {
        this.rewrite = null;
        return;
      }
      const desc = this.describe();
      if (!desc) {
        this.rewrite = null;
        return;
      }
      if (this.showingRewrite && this.rewrite.key === desc.key) return;
      this.rewrite = null;
      // A redact or failure for this exact key stays redacted until the
      // source, the mapping or the context changes, or (transient failures
      // only) the component is mounted or reactivated again.
      if (desc.key === this.failedKey) return;
      this.pendingKey = desc.key;
      this.cancelRequest = requestDemoRewrite(
        { text: this.source, record: this.recordId },
        (result) => this.onResult(result),
        { allowRetry, el: this.$el }
      );
    },
    onResult(result) {
      this.cancelRequest = null;
      this.pendingKey = null;
      if (!this.active || !this.isDemoMode) return;
      const desc = this.describe();
      if (!desc || desc.key !== result.key) return;
      if (result.status !== 'ok') {
        this.failedKey = result.key;
        this.failReason = result.reason || 'backend';
        this.failDiagnostics = { reasons: result.reasons || [], shapes: result.shapes || {} };
        return;
      }
      const verdict = checkRewrite(this.source, result.text, result.fakes);
      if (!verdict.ok) {
        reportRejectedRewrite(verdict.hits);
        this.failedKey = result.key;
        this.failReason = 'browser';
        return;
      }
      this.rewrite = {
        key: result.key,
        raw: result.text,
        text: demoBrandText(result.text),
        fakes: result.fakes,
        source: this.source,
        record: this.recordId,
        context: this.context,
      };
    },
    // More names were learned: the fakes map for this text may have changed
    // (a new request), and a shown rewrite is checked again either way.
    onNameMapChange() {
      if (!this.active || !this.isDemoMode) return;
      const desc = this.describe();
      if (this.rewrite) {
        if (!desc || desc.key !== this.rewrite.key) {
          this.sync();
          return;
        }
        const verdict = checkRewrite(this.source, this.rewrite.raw, this.rewrite.fakes);
        if (!verdict.ok) {
          reportRejectedRewrite(verdict.hits);
          this.failedKey = this.rewrite.key;
          this.failReason = 'browser';
          this.rewrite = null;
        }
        return;
      }
      if (this.cancelRequest ? desc?.key !== this.pendingKey : desc && desc.key !== this.failedKey) this.sync();
    },
    // Census category (counts only): readable, pending, empty, oversized,
    // backend, browser, malformed, http, rate-limit, timeout, network or off.
    censusState() {
      if (!this.isDemoMode) return 'off';
      if (this.showingRewrite) return 'readable';
      const desc = this.describe();
      if (!desc) return 'empty';
      if (!desc.sendable) return 'oversized';
      if (desc.key === this.failedKey) return this.failReason || 'backend';
      return 'pending';
    },
    // The non-blank text node values the renderer produces for the shown
    // rewrite, which the engine compares against what is actually in the DOM.
    expectedTextNodes() {
      if (!this.renderer) return this.shownText.trim() ? [this.shownText] : [];
      const template = document.createElement('template');
      template.innerHTML = this.html;
      const out = [];
      const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT, null);
      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim()) out.push(node.nodeValue);
      }
      return out;
    },
    syncRegistration() {
      const el = this.$el;
      if (!el || el.nodeType !== 1) return;
      if (this.showingRewrite && this.active) {
        const wasRegistered = this.registered;
        this.registered = true;
        if (!registerDemoRewrite(el, this.expectedTextNodes())) {
          // The DOM is not what was validated: stay redacted for this key.
          this.registered = false;
          this.failedKey = this.rewrite.key;
          this.failReason = 'browser';
          this.rewrite = null;
        } else if (!wasRegistered) {
          noteDemoTextReadable();
        }
      } else if (this.registered) {
        this.registered = false;
        clearDemoRewrite(el);
      }
    },
  },
  render() {
    const props = { class: 'sensitive', 'data-demo-blur': '' };
    if (this.renderer) return h(this.tag, { ...props, innerHTML: this.html });
    return h(this.tag, props, this.shownText);
  },
};
</script>
